import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../../helpers/api";
import { BASE_API } from "../../../../helpers/apiUrl";
import { toast } from "../../../../helpers/toast";
import {
  BulkCreatePriceMatrixParams,
  ErrorMessage,
  GetAllPaymentData,
  GetAllPaymentParams,
  GetALLPriceMartrixLogsParams,
  GetALLPriceMartrixParams,
  GetAllPriceMatrixData,
  GetAllPriceMatrixLogsData,
  GetSinglePriceMatrixParams,
  Params,
  PostRequest,
} from "../../../../types/redux";

// get all users
export const getAllPaymentAsync = createAsyncThunk<
  GetAllPaymentData[],
  Params<GetAllPaymentParams>,
  {
    rejectValue: ErrorMessage;
  }
>("payment/getAllPayment", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getAllPayment, {
      params,
    });

    return data.data?.data as GetAllPaymentData[];
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get all payment fail");
    return rejectWithValue(error);
  }
});

// get count payment
export const getCountPaymentAsync = createAsyncThunk<
  number,
  Params<GetAllPaymentParams>,
  {
    rejectValue: ErrorMessage;
  }
>("payment/getCountPayment", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getCountPayment, {
      params,
    });
    return data.data?.count || 0;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get user count payment fail");
    return rejectWithValue(error);
  }
});

// get tower all amount
export const getTotalAmountAsync = createAsyncThunk<
  GetAllPaymentData, // <-- Changed from array type to object type
  Params<Record<string, unknown>>,
  { rejectValue: ErrorMessage }
>("payment/getTotalAmount", async ({ params }, { rejectWithValue }) => {
  try {
    const { data } = await AXIOS.get(BASE_API.getTotalAmount, { params });

    return data as GetAllPaymentData; // Ensure it matches your type
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Failed to fetch payment data");
    return rejectWithValue(error);
  }
});

// update a status
export const updateStatusAsync = createAsyncThunk<null, any>(
  "payment/updateStatus",
  async ({ data, others: { reRender, id, params } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updateStatus}/${id}`, data, {
        params: { ...params },
      });

      reRender(true);
      toast("success", "Update successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Update fail");
      return rejectWithValue(error);
    }
  }
);

// Get Finance Balance
export const getFinanceBalanceAsync = createAsyncThunk<
  number,
  string,
  {
    rejectValue: ErrorMessage;
  }
>("payment/getFinanceBalance", async (token, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getFinanceBalance, {
      // headers: {
      //   Authorization: `Bearer ${token}`,
      // },
    });

    return data.data?.balance;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get user finance balance fail");
    return rejectWithValue(error);
  }
});

// Create Price Matrix
export const bulkCreatePriceMatrixAsync = createAsyncThunk<
  null,
  PostRequest<any, BulkCreatePriceMatrixParams>,
  { rejectValue: ErrorMessage }
>(
  "np-network/bulkCreatePriceMatrix",
  async ({ data, others }, { rejectWithValue }) => {
    try {
      const response = await AXIOS.post(BASE_API.bulkCreatePriceMatrix, data);

      // Call reRender if provided
      others?.reRender?.(true);

      // Show success toast
      toast("success", "Price matrix uploaded successfully");

      return response?.data;
    } catch (err: any) {
      const errors = err as AxiosError;
      const error = errors.response?.data as any;

      // Collect all errors
      let errorMessage = "";

      if (Array.isArray(error?.errors)) {
        // Flatten all messages into one string
        errorMessage = error.errors
          .map((item: any) =>
            Array.isArray(item?.errors) ? item.errors.join(", ") : ""
          )
          .filter(Boolean)
          .join(" | "); // separate groups with |
      } else {
        errorMessage =
          error?.error || error?.message || "Please fill in all required fields";
      }

      toast("error", errorMessage);

      return rejectWithValue(error);
    }
  }
);

// get all price matrix
export const getAllPriceMatrixAsync = createAsyncThunk<
  GetAllPriceMatrixData,
  Params<GetALLPriceMartrixParams>,
  {
    rejectValue: ErrorMessage;
  }
>("payment/getAllPriceMatrix", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getAllPriceMatrix, {
      params,
    });

    return data?.data as GetAllPriceMatrixData;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get all price matrix fail");
    return rejectWithValue(error);
  }
});

//  get Single Price Matrix
export const getSinglePriceMatrixAsync = createAsyncThunk<
  any,
  Params<GetSinglePriceMatrixParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "SingleTowerLog/getSinglePriceMatrix",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(
        `${BASE_API.getSinglePriceMatrix}/${params?.id}`
      );

      return data?.data as any;
      // return data.data as any;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Price matrix single data get fail");
      return rejectWithValue(error);
    }
  }
);

// update price matrix
// export const updatePriceMatrixAsync = createAsyncThunk<null, any>(
//   "payment/updatePriceMatrix",
//   async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
//     try {
//       await AXIOS.patch(`${BASE_API.updatePriceMatrix}/${id}`, data);

//       reRender(true);
//       toast("success", "Price matrix updated successfully");
//       return null;
//     } catch (err) {
//       const errors = err as AxiosError;
//       const error = errors.response?.data as ErrorMessage;
//       toast("error", error?.message || "Price matrix updated fail");
//       return rejectWithValue(error);
//     }
//   }
// );

// update price matrix
export const updatePriceMatrixAsync = createAsyncThunk<null, any>(
  "payment/updatePriceMatrix",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updatePriceMatrix}/${id}`, data);

      reRender(true);
      toast("success", "Price matrix updated successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as any;

      // Pick error message directly from API response
      let errorMessage =
        error?.error || error?.message || "Price matrix update failed";

      toast("error", errorMessage);

      return rejectWithValue(error);
    }
  }
);

// get all price matrix logs
export const getAllPriceMatrixLogsAsync = createAsyncThunk<
  GetAllPriceMatrixLogsData,
  Params<GetALLPriceMartrixLogsParams>,
  {
    rejectValue: ErrorMessage;
  }
>("payment/getAllPriceMatrixLogs", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getAllPriceMatrixLogs, {
      params,
    });

    return data?.data as GetAllPriceMatrixLogsData;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get all price matrix logs fail");
    return rejectWithValue(error);
  }
});
